/**
 * Canonical Hisaab schema. Single source of truth.
 * Mirrored in:
 *  - scripts/parse_emails.py        (Python pipeline output)
 *  - mcp/server.ts                   (MCP resource shape)
 *  - future: ios/Hisaab/Models.swift (App Intent AppEntity)
 *  - future: ios/Hisaab/Wallet.swift (PassKit auxiliary fields)
 */

export const SCHEMA_VERSION = 1 as const;

export const CATEGORIES = [
  'food_delivery',
  'grocery',
  'fashion',
  'beauty',
  'electronics',
  'lifestyle',
  'travel',
  'entertainment',
  'health',
  'home',
  'mobility',
  'services',
  'other',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const TIERS = [
  'aam_aadmi',
  'madhyam_varg',
  'khaata_peeta',
  'khaandani',
  'sheth',
] as const;
export type Tier = (typeof TIERS)[number];

export const TIER_THRESHOLDS_MONTHLY: Record<Tier, number> = {
  aam_aadmi: 0,
  madhyam_varg: 10_000,
  khaata_peeta: 30_000,
  khaandani: 75_000,
  sheth: 150_000,
};

export const TIER_DISPLAY: Record<Tier, { name: string; deva: string; tagline: string; kicker: string }> = {
  aam_aadmi: {
    name: 'Aam Aadmi',
    deva: 'आम आदमी',
    tagline: 'Every rupee earns its line in the ledger.',
    kicker: 'The diary begins. कलम uthao.',
  },
  madhyam_varg: {
    name: 'Madhyam Varg',
    deva: 'मध्यम वर्ग',
    tagline: 'EMI is a discipline. So is this book.',
    kicker: 'Halfway up the page. हिसाब tight.',
  },
  khaata_peeta: {
    name: 'Khaata-Peeta',
    deva: 'खाता-पीता',
    tagline: 'Mauj-masti, but the column still balances.',
    kicker: 'Comfortable, counted, classy.',
  },
  khaandani: {
    name: 'Khaandani',
    deva: 'खानदानी',
    tagline: 'Old money keeps a cleaner book than new money.',
    kicker: 'Quiet wealth. Loud ledger.',
  },
  sheth: {
    name: 'Sheth',
    deva: 'सेठ',
    tagline: '₹1.5L a month, every month, and you still tally.',
    kicker: 'Sheth. The shop runs on this page.',
  },
};

export type Source = 'gmail' | 'outlook' | 'sms' | 'wallet' | 'manual';

export interface Transaction {
  id: string;
  source: Source;
  source_ref: string;
  merchant_id: string;
  merchant_display: string;
  amount_inr: number;
  items: string[];
  category: Category;
  date: string; // YYYY-MM-DD
  confidence: number; // 0..1
  detected_at: string; // ISO 8601
}

export interface Insights {
  tier: Tier;
  biggest: Transaction | null;
  most_frequent_merchant: string | null;
  impulse_score: number; // 0-100, higher = more impulse-shoppy
  weekend_share: number; // 0..1
  late_night_share: number; // 0..1
}

export interface Summary {
  schema_version: typeof SCHEMA_VERSION;
  generated_at: string;
  window: { from: string; to: string; days: number };
  transaction_count: number;
  totals: {
    inr: number;
    monthly_avg: number;
    by_category: Partial<Record<Category, number>>;
    by_merchant: Record<string, number>;
    by_month: Record<string, number>;
  };
  insights: Insights;
  transactions: Transaction[];
}

export const MERCHANT_DISPLAY: Record<string, string> = {
  amazon: 'Amazon',
  flipkart: 'Flipkart',
  myntra: 'Myntra',
  ajio: 'AJIO',
  nykaa: 'Nykaa',
  swiggy: 'Swiggy',
  zomato: 'Zomato',
  zepto: 'Zepto',
  blinkit: 'Blinkit',
  bigbasket: 'BigBasket',
  bookmyshow: 'BookMyShow',
  makemytrip: 'MakeMyTrip',
  uber: 'Uber',
  ola: 'Ola',
  tata1mg: 'Tata 1mg',
  pharmeasy: 'PharmEasy',
  apollo: 'Apollo 24/7',
  boat: 'boAt',
  mamaearth: 'Mamaearth',
  lenskart: 'Lenskart',
  firstcry: 'FirstCry',
  decathlon: 'Decathlon',
  croma: 'Croma',
  reliancedigital: 'Reliance Digital',
  jiomart: 'JioMart',
  urbancompany: 'Urban Company',
  cult: 'Cult.fit',
  tatacliq: 'Tata CLiQ',
  meesho: 'Meesho',
  pepperfry: 'Pepperfry',
  // Empire-ops merchants (real data set)
  razorpay: 'Razorpay',
  godaddy: 'GoDaddy',
  hetzner: 'Hetzner',
  adobe: 'Adobe',
  openrouter: 'OpenRouter',
  google_play: 'Google Play',
};

export function tierFor(monthlyAvg: number): Tier {
  if (monthlyAvg >= TIER_THRESHOLDS_MONTHLY.sheth) return 'sheth';
  if (monthlyAvg >= TIER_THRESHOLDS_MONTHLY.khaandani) return 'khaandani';
  if (monthlyAvg >= TIER_THRESHOLDS_MONTHLY.khaata_peeta) return 'khaata_peeta';
  if (monthlyAvg >= TIER_THRESHOLDS_MONTHLY.madhyam_varg) return 'madhyam_varg';
  return 'aam_aadmi';
}

export const CATEGORY_DISPLAY: Record<Category, string> = {
  food_delivery: 'Food delivery',
  grocery: 'Grocery',
  fashion: 'Fashion',
  beauty: 'Beauty',
  electronics: 'Electronics',
  lifestyle: 'Lifestyle',
  travel: 'Travel',
  entertainment: 'Entertainment',
  health: 'Health',
  home: 'Home',
  mobility: 'Mobility',
  services: 'Services',
  other: 'Other',
};

export const formatINR = (n: number): string => {
  const rounded = Math.round(n);
  const s = rounded.toString();
  if (rounded < 1000) return `₹${s}`;
  if (rounded < 100_000) return `₹${rounded.toLocaleString('en-IN')}`;
  if (rounded < 10_000_000) return `₹${(rounded / 100_000).toFixed(rounded % 100_000 === 0 ? 0 : 2)}L`;
  return `₹${(rounded / 10_000_000).toFixed(2)}Cr`;
};
